import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Stack,
  Typography
} from '@mui/material';
import { Home as HomeIcon, LocalShipping as PreorderIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { useTranslation } from '../../hooks/useTranslation';
import { publicCollectionService } from '../../services/publicCollectionService';

function PublicCollectionPage() {
  const { userId } = useParams();
  const { t, currentLanguage } = useTranslation();
  const [toys, setToys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorKey, setErrorKey] = useState(null);
  const [userCurrency, setUserCurrency] = useState('USD');

  useEffect(() => {
    async function loadPublicCollection() {
      if (!userId) {
        setErrorKey('invalidLink');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const publicData = await publicCollectionService.getPublicCollection(userId);
        if (!publicData) {
          setErrorKey('notShared');
          setToys([]);
          setUserCurrency('USD');
          return;
        }

        setToys(Array.isArray(publicData.items) ? publicData.items : []);
        setUserCurrency(publicData.currency || 'USD');
        setErrorKey(null);
      } catch (err) {
        console.error('Failed to load public collection:', err);
        setErrorKey('loadError');
      } finally {
        setLoading(false);
      }
    }

    loadPublicCollection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, currentLanguage]);

  const stats = useMemo(() => {
    const totals = toys.reduce(
      (acc, toy) => {
        const price = toy.preorder_price && toy.preorder_price > 0 && !toy.createdDate
          ? toy.preorder_price
          : (toy.price || 0);
        return {
          totalValue: acc.totalValue + price,
          totalQuantity: acc.totalQuantity + (toy.qty || 1)
        };
      },
      { totalValue: 0, totalQuantity: 0 }
    );

    return {
      totalValue: totals.totalValue,
      totalQuantity: totals.totalQuantity,
      totalToys: toys.length
    };
  }, [toys]);

  const getImageUrl = (imageUrl, toyName) => {
    if (imageUrl) {
      return imageUrl;
    }
    const encodedName = encodeURIComponent(toyName || 'Toy');
    return `https://placehold.co/600x400/e0e0e0/666666?text=${encodedName}`;
  };

  const formatDate = (date) => {
    if (!date) return '';
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }
    return format(parsed, 'yyyy/MM/dd');
  };

  // Generate meta tags for social sharing
  const getMetaTags = () => {
    const baseUrl = window.location.origin;
    const publicUrl = `${baseUrl}/public/${userId}`;
    
    // Get preview image - use first toy's image if available, otherwise use banner
    let previewImage;
    if (toys.length > 0 && toys[0]?.imageUrl) {
      const toyImage = toys[0].imageUrl;
      // Ensure absolute URL
      previewImage = toyImage.startsWith('http://') || toyImage.startsWith('https://')
        ? toyImage
        : `${baseUrl}${toyImage.startsWith('/') ? '' : '/'}${toyImage}`;
    } else {
      previewImage = `${baseUrl}/banner.jfif`;
    }
    
    const formattedValue = Math.round(stats.totalValue).toLocaleString();
    
    const title = errorKey
      ? 'ToyPort - Toy Collection Manager'
      : `Toy Collection - ${stats.totalToys} ${stats.totalToys === 1 ? 'Toy' : 'Toys'} • ${userCurrency} ${formattedValue}`;
    
    const description = errorKey
      ? 'Toy collection management application'
      : `Explore this amazing toy collection featuring ${stats.totalToys} ${stats.totalToys === 1 ? 'item' : 'items'} with a total value of ${userCurrency} ${formattedValue}. ${stats.totalQuantity} ${stats.totalQuantity === 1 ? 'piece' : 'pieces'} in total.`;
    
    return {
      title,
      description,
      url: publicUrl,
      image: previewImage,
      siteName: 'ToyPort'
    };
  };

  const metaTags = getMetaTags();

  if (loading) {
    return (
      <Box sx={{ minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>{metaTags.title}</title>
        <meta name="description" content={metaTags.description} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={metaTags.url} />
        <meta property="og:title" content={metaTags.title} />
        <meta property="og:description" content={metaTags.description} />
        <meta property="og:image" content={metaTags.image} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content={metaTags.siteName} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={metaTags.url} />
        <meta name="twitter:title" content={metaTags.title} />
        <meta name="twitter:description" content={metaTags.description} />
        <meta name="twitter:image" content={metaTags.image} />
        
        {/* Additional meta tags */}
        <meta name="twitter:site" content="@ToyPort" />
        <link rel="canonical" href={metaTags.url} />
      </Helmet>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 6 }}>
      <Container maxWidth="lg">
        <Stack spacing={3}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {t('publicCollection.title')}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                {t('publicCollection.subtitle', { collectorId: userId?.slice(0, 8) || '...' })}
              </Typography>
            </Box>
            <Button
              component={RouterLink}
              to="/"
              startIcon={<HomeIcon />}
              variant="outlined"
            >
              {t('publicCollection.backToLanding')}
            </Button>
          </Stack>

          {errorKey && (
            <Alert severity="error">
              {t(`publicCollection.${errorKey}`)}
            </Alert>
          )}

          {!errorKey && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('publicCollection.viewOnlyNotice', { currency: userCurrency })}
            </Alert>
          )}

          {!errorKey && (
            <Card sx={{ p: 3 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="space-between">
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {stats.totalToys}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('collection.summary.totalToys')}
                  </Typography>
                </Box>
                <Divider flexItem orientation={"vertical"} sx={{ display: { xs: 'none', sm: 'block' } }} />
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {userCurrency} {Math.round(stats.totalValue).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('collection.summary.totalValue')}
                  </Typography>
                </Box>
                <Divider flexItem orientation={"vertical"} sx={{ display: { xs: 'none', sm: 'block' } }} />
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {stats.totalQuantity}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('collection.summary.totalQuantity')}
                  </Typography>
                </Box>
              </Stack>
            </Card>
          )}

          {!errorKey && toys.length === 0 && (
            <Card sx={{ p: 5, textAlign: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                {t('publicCollection.emptyTitle')}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t('publicCollection.emptyDescription')}
              </Typography>
            </Card>
          )}

          {!errorKey && toys.length > 0 && (
            <Grid container spacing={3}>
              {toys.map((toy) => {
                const price = toy.preorder_price && toy.preorder_price > 0 && !toy.createdDate
                  ? toy.preorder_price
                  : (toy.price || 0);

                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={toy.id}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ height: 200, bgcolor: 'grey.100' }}>
                        <CardMedia
                          component="img"
                          height="200"
                          image={getImageUrl(toy.imageUrl, toy.name)}
                          alt={toy.name || t('publicCollection.untitledToy')}
                          sx={{ objectFit: 'cover', width: '100%' }}
                        />
                      </Box>
                      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                        <Stack spacing={1} sx={{ mb: 2 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {toy.name || t('publicCollection.untitledToy')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {toy.brand || t('publicCollection.unknownBrand')}
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {toy.series && <Chip label={toy.series} size="small" variant="outlined" />}
                            {toy.type && <Chip label={toy.type} size="small" variant="outlined" />}
                            {toy.scale && <Chip label={toy.scale} size="small" variant="outlined" />}
                            {toy.code && <Chip label={`${t('common.code')}: ${toy.code}`} size="small" />}
                          </Stack>
                        </Stack>

                        <Box sx={{ mt: 'auto' }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                              {userCurrency} {Math.round(price).toLocaleString()}
                            </Typography>
                            {toy.preorder_price && toy.preorder_price > 0 && !toy.createdDate && (
                              <Chip
                                icon={<PreorderIcon />}
                                label={t('common.preorder')}
                                size="small"
                                color="warning"
                              />
                            )}
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            {t('common.quantity')}: {toy.qty || 1}
                          </Typography>
                          {formatDate(toy.createdDate) && (
                            <Typography variant="body2" color="text.secondary">
                              {t('common.created')}: {formatDate(toy.createdDate)}
                            </Typography>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Stack>
      </Container>
    </Box>
    </>
  );
}

export default PublicCollectionPage;
